/**
 * Indicador de SLA legible (v3).
 *
 * Reemplaza el valor crudo "-33m" / "1h40m" por texto explícito con tono
 * semántico, para que el operador entienda el estado de un vistazo sin
 * conocer la convención de antemano:
 *   - Vencido    → rojo   ("Vencido hace 33 min")
 *   - Por vencer → ámbar  ("Vence en 12 min")
 *   - A tiempo   → neutro ("Vence en 1h 40min")
 *
 * Los colores usan tokens semánticos (--error/--warning/surface) que ya
 * adaptan contraste entre modo claro y oscuro.
 */
import { slaLabel, type SlaTone } from "@/lib/format";
import { cn } from "@/lib/cn";

const TONE_CLASSES: Record<SlaTone, string> = {
  overdue: "bg-error-bg text-error-text border-error-border",
  soon: "bg-warning-bg text-warning-text border-warning-border",
  ok: "bg-surface-2 text-text-muted border-default",
};

const DOT_CLASSES: Record<SlaTone, string> = {
  overdue: "bg-error-text",
  soon: "bg-warning-text",
  ok: "bg-text-disabled",
};

interface SlaPillProps {
  remainingSec: number;
  /** `full` muestra el texto explícito; `compact` omite el prefijo para chips angostos. */
  variant?: "full" | "compact";
  className?: string;
}

export function SlaPill({ remainingSec, variant = "full", className }: SlaPillProps) {
  const sla = slaLabel(remainingSec);
  const label = variant === "compact" ? sla.short : sla.text;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5",
        "font-sans text-2xs font-medium tabular-nums whitespace-nowrap",
        TONE_CLASSES[sla.tone],
        className,
      )}
      title={sla.text}
    >
      <span
        aria-hidden="true"
        className={cn("w-1.5 h-1.5 rounded-full shrink-0", DOT_CLASSES[sla.tone])}
      />
      {label}
    </span>
  );
}
